import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { AccountAction } from 'dashboard/models/account-action';
import { ProductFeatureName } from 'dashboard/models/organization';
import { WorkflowStatus } from 'dashboard/models/workflow';
import { setFilters, setSortParams } from 'dashboard/redux/actions/account-actions';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  activateAccountAction,
  archiveAccountAction,
  createAccountAction,
  deactivateAccountAction,
} from 'dashboard/redux/thunk-actions/account-action';
import { loadAccountActions } from 'dashboard/redux/thunk-actions/account-actions';
import DashboardAccountActionsView from 'dashboard/views/DashboardAccountActionsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { generateWorkflowToken } from 'lib/workflows';

const DashboardAccountActionsRoute = (
  props: PropsFromRedux & RouteComponentProps<{ accountTypeId: string }>,
) => {
  const navigate = useNavigate();
  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.AccountsDashboard,
    props.currentEnvironment.type,
  );

  const { loading: loadingAccountActions, callback: loadAccountActions } = useApiLoader({
    loader: async () => {
      await props.loadAccountActions(
        props.match.params.accountTypeId,
        props.filters,
        props.sortParams,
      );
    },
    deps: [props.match.params.accountTypeId, props.filters, props.sortParams],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account actions.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: archiveAccountActionError, callback: handleArchiveAccountAction } = useApiCallback(
    async (accountAction: AccountAction) => {
      await props.archiveAccountAction(accountAction.id);
      await loadAccountActions();
      props.showMessage('Successfully archived account action.', MessageType.Success);
    },
    [props.archiveAccountAction, props.showMessage, loadAccountActions],
  );

  useApiErrorHandler({
    error: archiveAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to archive account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: activateAccountActionError, callback: handleActivateAccountAction } =
    useApiCallback(
      async (accountAction: AccountAction) => {
        await props.activateAccountAction(accountAction.id);
        await loadAccountActions();
        props.showMessage('Successfully activated account action.', MessageType.Success);
      },
      [props.activateAccountAction, props.showMessage, loadAccountActions],
    );

  useApiErrorHandler({
    error: activateAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to activate account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: deactivateAccountActionError, callback: handleDeactivateAccountAction } =
    useApiCallback(
      async (accountAction: AccountAction) => {
        await props.deactivateAccountAction(accountAction.id);
        await loadAccountActions();
        props.showMessage('Successfully deactivated account action.', MessageType.Success);
      },
      [props.deactivateAccountAction, props.showMessage, loadAccountActions],
    );

  useApiErrorHandler({
    error: deactivateAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to deactivate account action.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { handleOmniSearch, handleSortUpdate } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    setFilters: props.setFilters,
    setSortParams: props.setSortParams,
  });

  const handleStatusFilterUpdate = (status: WorkflowStatus[]) => {
    props.setFilters({ status });
  };

  const { error: handleCreateAccountActionError, callback: handleCreateAccountAction } =
    useApiCallback(async () => {
      const { data } = await props.createAccountAction(
        props.match.params.accountTypeId,
        `Account Action #${generateWorkflowToken().slice(0, 4)}`,
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const accountActionWorkflowVersionId = data.relationships['latest-draft-version'].data.id;

      props.showMessage('Successfully created account action.', MessageType.Success, false);
      navigate(
        `/dashboard/account-types/${props.match.params.accountTypeId}/actions/${data.id}/${accountActionWorkflowVersionId}`,
      );
    }, [props.showMessage, props.createAccountAction, props.match.params.accountTypeId]);

  useApiErrorHandler({
    error: handleCreateAccountActionError,
    onError: () =>
      props.showMessage('Failed to create account action.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  return (
    <DashboardAccountActionsView
      isLoading={loadingAccountActions}
      productFeatureIsDisabled={productFeatureIsDisabled}
      accountTypeId={props.match.params.accountTypeId}
      accountActions={props.accountActions}
      filters={props.filters}
      sortParams={props.sortParams}
      handleOmniSearch={handleOmniSearch}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleSortUpdate={handleSortUpdate}
      handleCreateAccountAction={handleCreateAccountAction}
      handleArchiveAccountAction={handleArchiveAccountAction}
      handleActivateAccountAction={handleActivateAccountAction}
      handleDeactivateAccountAction={handleDeactivateAccountAction}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    accountActions: state.accountActions.accountActions,
    filters: state.accountActions.filters,
    sortParams: state.accountActions.sortParams,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  loadAccountActions,
  createAccountAction,
  archiveAccountAction,
  activateAccountAction,
  deactivateAccountAction,
  setFilters,
  setSortParams,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(withRouter(DashboardAccountActionsRoute));
